import React, { useEffect } from 'react';

const PerformanceOptimizer: React.FC = () => {
  useEffect(() => {
    // Preload critical resources - only essential ones
    const preloadLinks = [
      { rel: 'preload', href: '/CONSTRUCCIONES-GRANADA-LOGO.png', as: 'image', fetchpriority: 'high' },
      { rel: 'dns-prefetch', href: 'https://fonts.googleapis.com' },
      { rel: 'dns-prefetch', href: 'https://fonts.gstatic.com' }
    ];

    preloadLinks.forEach(({ rel, href, as, fetchpriority }) => {
      const link = document.createElement('link');
      link.rel = rel;
      link.href = href;
      if (as) link.setAttribute('as', as);
      if (fetchpriority) link.setAttribute('fetchpriority', fetchpriority);
      document.head.appendChild(link);
    });
    
    // Lazy load background images
    const lazyLoadBackgrounds = () => {
      requestAnimationFrame(() => {
        const elements = document.querySelectorAll('[data-bg]');
        const bgObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const element = entry.target as HTMLElement;
              const bgUrl = element.getAttribute('data-bg');
              if (bgUrl) {
                element.style.backgroundImage = `url('${bgUrl}')`;
                element.removeAttribute('data-bg');
                bgObserver.unobserve(element);
              }
            }
          });
        }, { rootMargin: '50px' });
        
        elements.forEach(el => bgObserver.observe(el));
      });
    };
    
    // Run background image lazy loading
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', lazyLoadBackgrounds);
    } else {
      lazyLoadBackgrounds();
    }

    // Monitor Core Web Vitals - only in development or when explicitly enabled
    if (process.env.NODE_ENV === 'development' || window.location.search.includes('vitals=true')) {
      import('web-vitals').then(({ onCLS, onFCP, onLCP, onTTFB }) => {
        onCLS(console.log);
        onFCP(console.log);
        onLCP(console.log);
        onTTFB(console.log);
      }).catch(() => {
        // Silently fail in production
      });
    }

    // Optimize images - use requestAnimationFrame to avoid forced reflow
    const optimizeImages = () => {
      requestAnimationFrame(() => {
        // Batch all DOM reads
        const images = document.querySelectorAll('img');
        const lazyImages = document.querySelectorAll('img[data-src]');
        
        // Batch all DOM writes
        images.forEach(img => {
          if (!img.loading) {
            img.loading = 'lazy';
          }
        });

        // Add intersection observer for images
        if ('IntersectionObserver' in window && lazyImages.length > 0) {
          const imageObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                const img = entry.target as HTMLImageElement;
                if (img.dataset.src) {
                  // Batch attribute changes
                  img.src = img.dataset.src;
                  img.removeAttribute('data-src');
                  imageObserver.unobserve(img);
                }
              }
            });
          });

          lazyImages.forEach(img => {
            imageObserver.observe(img);
          });
        }
      });
    };

    // Run after a short delay to ensure DOM is ready
    const timer = setTimeout(optimizeImages, 0);
    
    // Also run when DOM is fully loaded
    let domContentLoadedHandler: (() => void) | null = null;
    if (document.readyState === 'loading') {
      domContentLoadedHandler = optimizeImages;
      document.addEventListener('DOMContentLoaded', domContentLoadedHandler);
    } else {
      optimizeImages();
    }

    // Cleanup function
    return () => {
      clearTimeout(timer);
      if (domContentLoadedHandler) {
        document.removeEventListener('DOMContentLoaded', domContentLoadedHandler);
      }
      document.removeEventListener('DOMContentLoaded', lazyLoadBackgrounds);
      // Remove preload links on unmount (except critical ones)
      document.querySelectorAll('link[rel="preload"]:not([href*="CONSTRUCCIONES-GRANADA-LOGO"])').forEach(link => {
        link.remove();
      });
    };
  }, []);

  return null;
};

export default PerformanceOptimizer; 